import {
  optionACHS, optionBID, optionQD, optionTID, optionPRN, scheduledOption,
  optionSTAT, optionNOW,
} from '../global';


export interface MedicationAdminWindow {
  hour: number;   // hour and minute = time of day
  minute: number;
  window: number; // length of administration window.
}

// Simpler than using Timestamp or Date, because we just need an hour and minute.
export interface MedicationAdminTime {
  hour: number;
  minute: number;
}

// For insulin-type drugs: blood sugar and units.
export interface SlidingScaleOrder {
  bs: string;
  units: number | string;
}

// For insulin-type drugs: carbs and units.
export interface MealTimesOrder {
  carbs: string;
  units: number;
}

/**
 * The base class for provider orders.
 */
export class ProviderOrder {
  /**
   * The order's unique identifier.
   */
  id: string;

  /**
   * The ID of the medication ordered.
   */
  medId: string;

  /**
   * The name of the medication ordered.
   */
  medName: string;

  /**
   * Restricted medication
   */
  medControlledSubstance: boolean;

  /**
   * The route of administration for the medication ordered.
   */
  route: string;

  /**
   * The dosage of the medication ordered. This should be a multiple of the
   * medication's single dose amount (which is found in the medication's name).
   * As of the time of writing, dosage has no functionality within the MAR or
   * Provider Orders - it is merely a display value.
   */
  dosage: string;

  /**
   * The number of times a nurse has to administer a medication. This is the
   * number of times a nurse has to scan a medication's barcode to fully
   * administer it. This number is derived from the schedule, which
   * implies 1, 2, 3, or 4 administrations of the medicine.
   */
  quantity: number;

  /**
   * The number of times a nurse has administered a medication. This can be
   * greater than the quantity ordered.
   */
  quantityGiven: number;

  /**
   * An array of booleans indicating which times the medicine was
   * administered to the patient. E.g., if the type is TID
   * with 3 administration times, and the medicine was given only
   * the 2nd time, then the array should be [false, true, false].
   */
  whichGiven: boolean[];

  /**
   * Specifies the type of order: QD, BID, TID, ACHS, or PRN.
   * From this type, we can know if the medicine should be administered once,
   * twice, etc., and at what time of day.
   */
  type: string;

  /**
   * Additional notes or instructions about the order.
   */
  orderNotes: string;

  /**
   * The signature of the provider who ordered the medication.
   */
  signature: string;

  /**
   * whether or not it should be shown on the MAR schedule.
   */
  isScheduled: boolean;

  /**
   * The start of the time window to administer the first dose.
   * This is only needed when the time is not dictated by
   * the order type (e.g., BID is always at 09:00 and 21:00.)
   */
  startTime: MedicationAdminTime;

  /**
   * The last time when the medication was given (could be null -- 'never')
   * (PRN orders are at the nurses' discretion -- tylenol, etc.).
   */
  prnLastGivenTime: MedicationAdminTime | null;

  /**
   * Free text instructions on dosing. E.g., "every 2 hours as needed".
   */
  prnDosingInstructions: string;

  /**
   * Sliding scale options: usually for insulin medications. For
   * blood sugar, a certain number of units. It is a table of those
   * two values.
   */
  slidingScaleTable: SlidingScaleOrder[];

  /**
   * Like sliding scale options: usually for insulin medications. For
   * a certain number of carbs in the meal, administer a certain
   * number of units. It is a table of those two values.
   */
  mealTimesTable: MealTimesOrder[];

  /**
   * true if the provider order has been created as part of an
   * order set; false otherwise. Order Set provider orders are
   * not added to the MAR until they are applied. This is so the
   * administrator can add them during a simulation -- not as part
   * of the set up.
   */
  isInOrderSet: boolean;

  constructor(
    id?: string,
    medId?: string,
    medName?: string,
    medControlledSubstance?: boolean,
    route?: string,
    dosage?: string,
    quantity?: number,
    quantityGiven?: number,
    whichGiven?: boolean[],
    type?: string,
    orderNotes?: string,
    signature?: string,
    startTime?: MedicationAdminTime,
    prnLastGivenTime?: MedicationAdminTime | null,
    prnDosingInstructions?: string,
    slidingScaleTable?: SlidingScaleOrder[],
    mealTimesTable?: MealTimesOrder[],
    isInOrderSet?: boolean,
    isScheduled?: boolean,
  ) {
    this.id = id ? id : '' + Date.now(); // Set order ID to be the date in milliseconds
    this.medId = medId ?? '';
    this.medName = medName ?? '';
    this.medControlledSubstance = medControlledSubstance ?? false;
    this.route = route ?? '';
    this.dosage = dosage ?? '';
    this.quantity = quantity ?? 0;
    this.quantityGiven = quantityGiven ?? 0;
    this.whichGiven = whichGiven ?? [];
    this.type = type ?? '';
    this.orderNotes = orderNotes ?? '';
    this.signature = signature ?? '';
    this.startTime = startTime ?? { hour: 0, minute: 0 };
    this.prnLastGivenTime = prnLastGivenTime ?? null;
    this.prnDosingInstructions = prnDosingInstructions ?? '';
    this.slidingScaleTable = slidingScaleTable ?? [];
    this.mealTimesTable = mealTimesTable ?? [];
    this.isInOrderSet = isInOrderSet ?? false;
    this.isScheduled = isScheduled ?? true;
  }

  /**
   * @returns The ID of the order's medication.
   */
  getMedId() {
    return this.medId;
  }

  /**
   * @returns Whether or not the medication is a controlled substance
   */
  getMedControlledSubstance() {
    return this.medControlledSubstance;
  }

  /**
   * @returns Whether the medication has been fully/excessively administered or not.
   */
  get administered() {
    return this.quantityGiven >= this.quantity;
  }

  get isQD() {
    return this.type === optionQD;
  }

  get isBID() {
    return this.type === optionBID;
  }

  get isTID() {
    return this.type === optionTID;
  }

  get isACHS() {
    return this.type === optionACHS;
  }

  get isPRN() {
    return this.type === optionPRN;
  }

  get isNOW() {
    return this.type === optionNOW;
  }

  get isSTAT() {
    return this.type === optionSTAT;
  }

  get isSlidingScale() {
    return this.slidingScaleTable.length > 0;
  }
  get isMealTimes() {
    return this.mealTimesTable.length > 0;
  }

  get administeredString() {
    // For PRN, we show how many times it was administered and the last time.
    if (this.isPRN) {
      if (this.quantityGiven === 0) {
        return 'Not administered';
      }
      return `Given ${this.quantityGiven} time${this.quantityGiven === 1 ? '' : 's'}`;
    }
    if (this.quantityGiven > this.quantity) {
      return 'Overdosed';
    } else if (this.quantityGiven === this.quantity) {
      return 'Fully administered';
    } else if (this.quantityGiven > 0 && this.quantityGiven < this.quantity) {
      return 'Partially administered';
    } else {
      return 'Not administered';
    }
  }

  get prnLastGivenTimeString() {
    if (this.prnLastGivenTime === null) {
      return '';
    }
    return `${String(this.prnLastGivenTime!.hour).padStart(2, '0')}:${String(this.prnLastGivenTime!.minute).padStart(2, '0')}`;
  }

  /**
   * for a order of a certain type, return an array of MedicationAdminWindows
   */
  providerOrder2MedAdminWindows(): MedicationAdminWindow[] {

    if (this.type === optionBID) {
      return [
        { hour: 9, minute: 0, window: 60 },
        { hour: 21, minute: 0, window: 60 },
      ];
    } else if (this.type === optionTID) {
      return [
        { hour: 9, minute: 0, window: 60 },
        { hour: 16, minute: 0, window: 60 },
        { hour: 21, minute: 0, window: 60 },
      ];
    } else if (this.type === optionACHS) {
      return [
        { hour: 7, minute: 30, window: 60 },
        { hour: 11, minute: 30, window: 60 },
        { hour: 17, minute: 0, window: 60 },
        { hour: 22, minute: 0, window: 60 },
      ];
    } else if (this.type === optionQD) {
      return [{
        hour: this.startTime.hour,
        minute: this.startTime.minute,
        window: 60,
      }];
    } else {
      // STAT, NOW, etc.
      const now = new Date();
      return [{
        hour: now.getHours(),
        minute: now.getMinutes(),
        window: this.type === optionSTAT ? 30 : 60,
      }];
    }
  }

  /**
   * @returns array of readable strings describing the times to administer the meds.
   */
  providerOrder2MedSchedule(): string[] {

    // create a string like 09:00 - 10:00 or 13:41 - 14:11
    const order2DurationString = (sched: MedicationAdminWindow): string => {
      const startDate = new Date();
      startDate.setHours(sched.hour, sched.minute, 0);

      const endDate = new Date(startDate);
      // Take the start date and add the administer window (in minutes) to it.
      endDate.setTime(startDate.getTime() + sched.window * 60 * 1000);

      return `${startDate.getHours().toString().padStart(2, '0')}:${startDate.getMinutes().toString().padStart(2, '0')} - ` +
        `${(endDate.getHours()).toString().padStart(2, '0')}:${(endDate.getMinutes()).toString().padStart(2, '0')}`;
    }

    const sched = this.providerOrder2MedAdminWindows();
    return sched.map((s: MedicationAdminWindow, idx: number) => {
      return `Dose ${idx + 1} scheduled for ${order2DurationString(s)}`;
    });
  }

}
